"use client";

import type { ComponentType, SVGProps } from "react";
import type { ButtonColorVariant } from "./button-styles";
import type { RadioOption } from "./form-types";

import { cn } from "@homeinstead/ui";
import { buttonVariants } from "@homeinstead/ui/components/Button";
import Image from "next/image";
import { RecipientOptionsV1 } from "../CareSeeker/shared/form-options";
import { COLOR_VARIANT_MAP } from "./button-styles";

type PresetIcon = Exclude<RadioOption["icon"], "none" | "custom" | null>;

function getRecipientIcon(value: "parent" | "spouse" | "me" | "other") {
  return RecipientOptionsV1.find((option) => option.value === value)?.icon;
}

const PRESET_ICONS: Record<
  PresetIcon,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  other: getRecipientIcon("other")!,
  parent: getRecipientIcon("parent")!,
  self: getRecipientIcon("me")!,
  spouse: getRecipientIcon("spouse")!,
};

function OptionIcon({
  option,
  isPrimaryVariant,
}: {
  option: RadioOption;
  isPrimaryVariant: boolean;
}) {
  if (!option.icon || option.icon === "none") {
    return null;
  }

  if (option.icon === "custom" && option.customIconUrl) {
    return (
      <Image
        alt=""
        className="size-6 shrink-0"
        height={24}
        src={option.customIconUrl}
        width={24}
      />
    );
  }

  const IconComponent = PRESET_ICONS[option.icon as PresetIcon];
  if (!IconComponent) {
    return null;
  }

  return (
    <IconComponent
      aria-hidden="true"
      className={cn(
        "size-6 shrink-0",
        isPrimaryVariant
          ? "fill-friendly-base group-hover:fill-new-beginnings-base"
          : "fill-new-beginnings-base",
      )}
    />
  );
}

interface RadioInputOptionProps {
  colorVariant: ButtonColorVariant | null;
  isSelected: boolean;
  name: string;
  onChange: () => void;
  option: RadioOption;
}

export function RadioInputOption({
  colorVariant,
  isSelected,
  name,
  onChange,
  option,
}: RadioInputOptionProps) {
  const hasIcon = option.icon && option.icon !== "none";
  const variant = colorVariant ? COLOR_VARIANT_MAP[colorVariant] : "primary";
  const value = option.value ?? option.label;

  return (
    <label
      className={cn(
        buttonVariants({ variant }),
        "group flex h-12 w-full cursor-pointer justify-start gap-4 rounded-full px-6 font-semibold",
        "focus-within:outline focus-within:outline-2 focus-within:outline-new-beginnings-base focus-within:outline-offset-2",
        variant === "outline" && isSelected && "bg-new-beginnings-tints-800",
      )}
    >
      <input
        checked={isSelected}
        className="sr-only"
        name={name}
        onChange={onChange}
        // We need to support re-selection when users navigate back to a
        // pre-filled step. onChange doesn't fire when the user clicks an
        // already-selected option, so we trigger onChange from onClick to
        // ensure the handler still runs.
        onClick={() => {
          if (isSelected) {
            onChange();
          }
        }}
        type="radio"
        value={value}
      />
      {hasIcon && (
        <OptionIcon isPrimaryVariant={variant === "primary"} option={option} />
      )}
      <span>{option.label}</span>
    </label>
  );
}
